import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Phone, Mail, MapPin, Clock, ChevronDown, HelpCircle } from 'lucide-react'
import { Facebook, Instagram, Linkedin } from '../utils/brandIcons'
import { companyInfo } from '../data/data'

const faqs = [
    {
        question: "Comment devenir partenaire de SaDEAP ?",
        answer:
            "Pour devenir partenaire, contactez-nous par téléphone ou via le formulaire ci-dessus. Notre équipe vous proposera un rendez-vous pour analyser votre profil, vos besoins et définir ensemble les modalités de collaboration adaptées à votre activité agricole ou d'élevage.",
    },
    {
        question: "Quels sont les avantages d'être partenaire de SaDEAP ?",
        answer:
            "Nos partenaires bénéficient d'un accompagnement technique personnalisé, de l'accès à nos formations, de tarifs préférentiels sur la location de matériel agricole, d'un débouché stable pour leurs productions et d'un appui dans la commercialisation de leurs produits.",
    },
    {
        question: "Comment commander vos produits ?",
        answer:
            "Vous pouvez passer commande directement depuis la page Produits, par téléphone aux numéros indiqués, ou en remplissant le formulaire de contact. Pour les commandes en gros, nous proposons des tarifs préférentiels et la livraison est possible sur tout le territoire béninois.",
    },
    {
        question: "Proposez-vous des formations agricoles ?",
        answer:
            "Oui, SaDEAP organise régulièrement des formations sur l'agriculture durable, l'élevage responsable, la transformation des produits et la gestion d'exploitation. Consultez la page Services pour découvrir nos programmes et inscrivez-vous via le formulaire de contact.",
    },
    {
        question: "Dans quelles zones intervenez-vous ?",
        answer:
            "Notre siège est basé à Cotonou, mais nous intervenons dans tout le Bénin et collaborons avec des partenaires à travers l'Afrique. La livraison de produits et nos services d'accompagnement couvrent l'ensemble du territoire national.",
    },
    {
        question: "Vos produits sont-ils certifiés bio ?",
        answer:
            "Nos produits sont cultivés selon les principes de l'agriculture durable, sans pesticides chimiques ni OGM. Nous travaillons en permanence à l'obtention de certifications officielles et garantissons la traçabilité de toute notre production.",
    },
]

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    })

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitSuccess, setSubmitSuccess] = useState(false)
    const [openFaq, setOpenFaq] = useState(0)

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setIsSubmitting(true)

        // Simulate form submission
        setTimeout(() => {
            setIsSubmitting(false)
            setSubmitSuccess(true)
            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            })

            // Reset success message after 5 seconds
            setTimeout(() => setSubmitSuccess(false), 5000)
        }, 2000)
    }

    return (
        <div className="min-h-screen bg-neutral-light">
            {/* Hero Section */}
            <section className="bg-primary-green py-20 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-yellow/20 rounded-full blur-3xl"></div>
                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center text-white"
                    >
                        <h1 className="text-5xl lg:text-6xl font-bold mb-4">Contactez-Nous</h1>
                        <p className="text-xl text-white/90">Nous sommes à votre écoute pour tous vos besoins</p>
                    </motion.div>
                </div>
            </section>

            {/* Contact Info Cards */}
            <section className="py-12 -mt-8">
                <div className="container-custom">
                    <div className="grid md:grid-cols-4 gap-6">
                        {[
                            {
                                icon: <Phone className="w-6 h-6" />,
                                title: "Téléphone",
                                content: `${companyInfo.phone} / ${companyInfo.phone2}`,
                                link: `tel:${companyInfo.phone.replace(/\s/g, '')}`
                            },
                            {
                                icon: <Mail className="w-6 h-6" />,
                                title: "Email",
                                content: companyInfo.email,
                                link: `mailto:${companyInfo.email}`
                            },
                            {
                                icon: <MapPin className="w-6 h-6" />,
                                title: "Adresse",
                                content: companyInfo.address,
                                link: null
                            },
                            {
                                icon: <Clock className="w-6 h-6" />,
                                title: "Horaires",
                                content: "Lun-Ven: 8h-18h",
                                link: null
                            }
                        ].map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                whileHover={{ y: -5 }}
                                className="bg-white rounded-xl shadow-lg p-6 text-center"
                            >
                                <div className="w-14 h-14 bg-primary-yellow/20 rounded-full flex items-center justify-center mx-auto mb-4 text-primary-green text-xl">
                                    {item.icon}
                                </div>
                                <h3 className="font-semibold text-primary-green mb-2">{item.title}</h3>
                                {item.link ? (
                                    <a href={item.link} className="text-gray-600 hover:text-primary-green transition-colors">
                                        {item.content}
                                    </a>
                                ) : (
                                    <p className="text-gray-600">{item.content}</p>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Form & Map */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-12">
                        {/* Contact Form */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                        >
                            <div className="bg-white rounded-2xl shadow-xl p-8">
                                <h2 className="text-3xl font-bold text-primary-green mb-6">Envoyez-nous un message</h2>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-gray-700 font-medium mb-2">Nom complet</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                                                placeholder="Votre nom"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 font-medium mb-2">Email</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                                                placeholder="votre@email.com"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-gray-700 font-medium mb-2">Téléphone</label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                                                placeholder="+229 00 00 00 00"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 font-medium mb-2">Sujet</label>
                                            <select
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                                            >
                                                <option value="">Sélectionnez un sujet</option>
                                                <option value="information">Demande d'information</option>
                                                <option value="partnership">Partenariat</option>
                                                <option value="products">Produits</option>
                                                <option value="services">Services</option>
                                                <option value="other">Autre</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-gray-700 font-medium mb-2">Message</label>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            rows="5"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                                            placeholder="Votre message..."
                                        ></textarea>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={`w-full btn-primary ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                                    </button>

                                    {submitSuccess && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg"
                                        >
                                            Votre message a été envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.
                                        </motion.div>
                                    )}
                                </form>
                            </div>
                        </motion.div>

                        {/* Map & Additional Info */}
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            {/* Map */}
                            <div className="bg-white rounded-2xl shadow-xl overflow-hidden h-96">
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d126783.16617086966!2d2.3158585!3d6.3702928!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x102354e509f894f7%3A0xc8fde921f89849f6!2sCotonou%2C%20Benin!5e0!3m2!1sen!2sus!4v1647856514360!5m2!1sen!2sus"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="Map"
                                ></iframe>
                            </div>

                            {/* Social Media */}
                            <div className="bg-white rounded-2xl shadow-xl p-8">
                                <h3 className="text-2xl font-bold text-primary-green mb-6">Suivez-nous</h3>
                                <p className="text-gray-600 mb-6">
                                    Restez connecté avec nous sur les réseaux sociaux pour suivre nos actualités et nos activités.
                                </p>
                                <div className="flex space-x-4">
                                    <a
                                        href={companyInfo.socialMedia.facebook}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-12 h-12 bg-primary-green/10 rounded-full flex items-center justify-center hover:bg-primary-green hover:text-white transition-all duration-300 text-primary-green"
                                    >
                                        <Facebook className="w-5 h-5" />
                                    </a>
                                    <a
                                        href={companyInfo.socialMedia.instagram}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-12 h-12 bg-primary-green/10 rounded-full flex items-center justify-center hover:bg-primary-green hover:text-white transition-all duration-300 text-primary-green"
                                    >
                                        <Instagram className="w-5 h-5" />
                                    </a>
                                    <a
                                        href={companyInfo.socialMedia.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-12 h-12 bg-primary-green/10 rounded-full flex items-center justify-center hover:bg-primary-green hover:text-white transition-all duration-300 text-primary-green"
                                    >
                                        <Linkedin className="w-5 h-5" />
                                    </a>
                                </div>
                            </div>

                            {/* Quick FAQ Teaser */}
                            <div className="bg-primary-yellow/10 rounded-2xl p-8">
                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-primary-green rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                                        <HelpCircle className="w-6 h-6 text-primary-yellow" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-primary-green mb-2">Une question ?</h3>
                                        <p className="text-gray-600 text-sm mb-3">
                                            Consultez notre foire aux questions pour des réponses immédiates.
                                        </p>
                                        <a href="#faq" className="inline-flex items-center text-primary-green font-semibold hover:text-primary-light transition-colors text-sm">
                                            Voir la FAQ
                                            <ChevronDown className="ml-1 w-4 h-4" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section id="faq" className="section-padding bg-white">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <span className="inline-block px-4 py-2 bg-primary-yellow/20 text-primary-green rounded-full text-sm font-semibold mb-4">
                            FAQ
                        </span>
                        <h2 className="text-4xl lg:text-5xl font-bold text-primary-green mb-4">
                            Questions Fréquemment Posées
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Retrouvez ici les réponses aux questions les plus courantes sur SaDEAP, nos services et nos produits
                        </p>
                    </motion.div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {faqs.map((faq, index) => {
                            const isOpen = openFaq === index
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: index * 0.05 }}
                                    viewport={{ once: true }}
                                    className={`rounded-2xl border-2 transition-all duration-300 overflow-hidden ${isOpen
                                            ? 'border-primary-green bg-primary-green/5 shadow-lg'
                                            : 'border-gray-200 bg-white hover:border-primary-green/40'
                                        }`}
                                >
                                    <button
                                        onClick={() => setOpenFaq(isOpen ? -1 : index)}
                                        className="w-full flex items-center justify-between text-left px-6 py-5 focus:outline-none"
                                        aria-expanded={isOpen}
                                    >
                                        <div className="flex items-center pr-4">
                                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-4 flex-shrink-0 transition-colors ${isOpen ? 'bg-primary-green text-white' : 'bg-primary-yellow/20 text-primary-green'
                                                }`}>
                                                {String(index + 1).padStart(2, '0')}
                                            </span>
                                            <h3 className="font-semibold text-primary-green text-base md:text-lg">
                                                {faq.question}
                                            </h3>
                                        </div>
                                        <motion.div
                                            animate={{ rotate: isOpen ? 180 : 0 }}
                                            transition={{ duration: 0.3 }}
                                            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isOpen ? 'bg-primary-green text-white' : 'bg-gray-100 text-primary-green'
                                                }`}
                                        >
                                            <ChevronDown className="w-5 h-5" />
                                        </motion.div>
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                                className="overflow-hidden"
                                            >
                                                <div className="px-6 pb-6 pl-[4.5rem] text-gray-600 leading-relaxed">
                                                    {faq.answer}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            )
                        })}
                    </div>

                    {/* Still have questions CTA */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="max-w-3xl mx-auto mt-12 bg-primary-green rounded-2xl p-8 text-center text-white"
                    >
                        <h3 className="text-2xl font-bold mb-3">Vous avez une autre question ?</h3>
                        <p className="text-white/90 mb-6">
                            Notre équipe est à votre disposition pour répondre à toutes vos interrogations
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a
                                href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                                className="inline-flex items-center justify-center px-6 py-3 bg-primary-yellow text-primary-green font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-300"
                            >
                                <Phone className="w-5 h-5 mr-2" />
                                Nous appeler
                            </a>
                            <a
                                href={`mailto:${companyInfo.email}`}
                                className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-green transition-all duration-300"
                            >
                                <Mail className="w-5 h-5 mr-2" />
                                Nous écrire
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    )
}

export default Contact